/**
 * API client for Dossier backend
 */

import type {
  ChatRequest,
  ChatResponseWithConversation,
  ConversationListResponse,
  ConversationDetail,
  CompanyListItem,
  CompanyOverview,
  NewsFeed,
  ProductTaxonomy,
  KBStatus,
  StockData,
  QuarterlyRevenue,
  ReportTemplateInfo,
  GeneratedReport,
  ReportSummary,
  CompanySearchResult,
  CustomerListResponse,
  CustomerWinsResponse,
  CustomerLossesResponse,
  TopCustomersResponse,
  CustomerTabResponse,
  // Spike 13: Jobs
  JobListResponse,
  HiringMetricsResponse,
  HiringSignalsResponse,
  Department,
  // Admin Jobs & Logs
  AdminJobItem,
  ScheduledJobItem,
  JobQueueStats,
  ExceptionLogItem,
  LogStats,
  // User Statistics
  UserStatsSummary,
  GroupStats,
  ActiveUserStats,
  DailyActiveUsers,
  TopCompanyStats,
  TopQuestion,
  ActivityEventType,
  LogEventResponse,
  StartSessionResponse,
  EndSessionResponse,
} from '../types';

const API_BASE_URL = import.meta.env.VITE_API_URL || 'http://localhost:8001';

// Store access token globally for API calls
let _accessToken: string | null = null;

/**
 * Set the access token for API requests.
 * Called by auth context when user logs in.
 */
export function setAccessToken(token: string | null) {
  _accessToken = token;
}

/**
 * Get the current access token.
 */
export function getAccessToken(): string | null {
  return _accessToken;
}

/**
 * Fetch with auth headers using the shared API client.
 * Use this instead of raw fetch() for authenticated endpoints.
 */
export function getAuthHeaders(): Record<string, string> {
  const headers: Record<string, string> = {
    'Content-Type': 'application/json',
  };
  if (_accessToken) {
    headers['Authorization'] = `Bearer ${_accessToken}`;
  }
  return headers;
}

class APIError extends Error {
  constructor(
    public status: number,
    message: string
  ) {
    super(message);
    this.name = 'APIError';
  }
}

async function fetchAPI<T>(endpoint: string, options: RequestInit = {}): Promise<T> {
  const url = `${API_BASE_URL}${endpoint}`;

  const headers: HeadersInit = {
    'Content-Type': 'application/json',
    ...options.headers,
  };

  // Add authorization header if token is available
  if (_accessToken) {
    (headers as Record<string, string>)['Authorization'] = `Bearer ${_accessToken}`;
  }

  const response = await fetch(url, {
    ...options,
    headers,
  });

  if (!response.ok) {
    const error = await response.json().catch(() => ({}));
    throw new APIError(
      response.status,
      error.detail || `API request failed: ${response.statusText}`
    );
  }

  return response.json();
}

// Health check
export async function checkHealth(): Promise<{ status: string }> {
  return fetchAPI('/api/health');
}

// Companies
export async function listCompanies(): Promise<CompanyListItem[]> {
  return fetchAPI('/api/companies');
}

export async function getCompany(companyId: string): Promise<CompanyOverview['company']> {
  return fetchAPI(`/api/companies/${companyId}`);
}

export async function getCompanyOverview(companyId: string): Promise<CompanyOverview> {
  return fetchAPI(`/api/companies/${companyId}/overview`);
}

export async function getCompanyNews(
  companyId: string,
  options?: {
    days?: number;
    materiality?: string[];
    limit?: number;
    offset?: number;
  }
): Promise<NewsFeed> {
  const params = new URLSearchParams();
  if (options?.days) params.set('days', options.days.toString());
  if (options?.materiality) {
    options.materiality.forEach((m) => params.append('materiality', m));
  }
  if (options?.limit) params.set('limit', options.limit.toString());
  if (options?.offset) params.set('offset', options.offset.toString());

  const queryString = params.toString();
  const endpoint = `/api/companies/${companyId}/news${queryString ? `?${queryString}` : ''}`;
  return fetchAPI(endpoint);
}

export interface NewsDetailResponse {
  event: {
    id: string;
    company_id: string;
    company_name: string;
    event_type: string;
    materiality: string;
    title: string;
    source_url?: string;
    original_summary: string;
  };
  analysis: {
    headline: string;
    why_it_matters: string;
    the_big_picture: string;
    key_details: string[];
    whats_next: string;
    analyst_take: string;
    related_topics: string[];
    source_url: string | null;
    source_name: string | null;
  };
}

export async function getNewsDetail(eventId: string): Promise<NewsDetailResponse> {
  return fetchAPI(`/api/companies/news/${eventId}`);
}

export async function getCompanyProducts(companyId: string): Promise<ProductTaxonomy> {
  return fetchAPI(`/api/companies/${companyId}/products`);
}

// Spike 13: Portfolio with subsidiaries
import type {
  CompanyPortfolio,
  CompetitorFeatureMatrix,
  TopCompetitor,
  ProductProfile,
} from '../types';

export async function getCompanyPortfolio(companyId: string): Promise<CompanyPortfolio> {
  return fetchAPI(`/api/companies/${companyId}/portfolio`);
}

// Spike 13: Product profile detail
export async function getProductProfile(
  companyId: string,
  productId: string
): Promise<ProductProfile> {
  return fetchAPI(`/api/companies/${companyId}/products/${productId}`);
}

// Spike 13: Product competitors
export interface TopCompetitorsResponse {
  product_id: string;
  product_name: string;
  competitors: TopCompetitor[];
}

export async function getProductCompetitors(
  productId: string,
  limit: number = 5
): Promise<TopCompetitorsResponse> {
  return fetchAPI(`/api/products/${productId}/competitors?limit=${limit}`);
}

// Spike 13: Competitive matrix
export async function getCompetitiveMatrix(
  productId: string,
  competitorId: string
): Promise<CompetitorFeatureMatrix> {
  return fetchAPI(`/api/products/${productId}/competitive-matrix?competitor_id=${competitorId}`);
}

export async function getKBStatus(companyId: string): Promise<KBStatus> {
  return fetchAPI(`/api/companies/${companyId}/kb-status`);
}

// Chat
export async function sendChatMessage(request: ChatRequest): Promise<ChatResponseWithConversation> {
  return fetchAPI('/api/chat', {
    method: 'POST',
    body: JSON.stringify(request),
  });
}

// Conversations
export async function listConversations(
  companyId?: string,
  limit: number = 10
): Promise<ConversationListResponse> {
  const params = new URLSearchParams();
  if (companyId) params.set('company_id', companyId);
  params.set('limit', limit.toString());
  return fetchAPI(`/api/chat/conversations?${params}`);
}

export async function getConversation(conversationId: string): Promise<ConversationDetail> {
  return fetchAPI(`/api/chat/conversations/${conversationId}`);
}

export async function deleteConversation(
  conversationId: string
): Promise<{ status: string; id: string }> {
  return fetchAPI(`/api/chat/conversations/${conversationId}`, {
    method: 'DELETE',
  });
}

// Stock data (public companies only)
export async function getStockData(companyId: string, period: string = '1mo'): Promise<StockData> {
  const params = new URLSearchParams({ period });
  return fetchAPI(`/api/companies/${companyId}/stock?${params}`);
}

// Quarterly revenue (public companies only)
export async function getQuarterlyRevenue(
  companyId: string,
  quarters: number = 12
): Promise<QuarterlyRevenue> {
  const params = new URLSearchParams({ quarters: quarters.toString() });
  return fetchAPI(`/api/companies/${companyId}/quarterly-revenue?${params}`);
}

// Reports
export async function getReportTemplates(): Promise<ReportTemplateInfo[]> {
  return fetchAPI('/api/reports/templates');
}

export async function getReportTemplate(reportType: string): Promise<unknown> {
  return fetchAPI(`/api/reports/templates/${reportType}`);
}

export async function generateReport(
  companyId: string,
  reportType: string,
  title?: string
): Promise<GeneratedReport> {
  const params = new URLSearchParams({
    company_id: companyId,
    report_type: reportType,
  });
  if (title) params.set('title', title);
  return fetchAPI(`/api/reports/generate?${params}`, { method: 'POST' });
}

export async function listReports(companyId?: string): Promise<ReportSummary[]> {
  const params = companyId ? `?company_id=${companyId}` : '';
  return fetchAPI(`/api/reports${params}`);
}

export async function getReport(reportId: string): Promise<GeneratedReport> {
  return fetchAPI(`/api/reports/${reportId}`);
}

export async function deleteReport(reportId: string): Promise<{ status: string; id: string }> {
  return fetchAPI(`/api/reports/${reportId}`, { method: 'DELETE' });
}

// Executives / Leadership
export interface ExecutiveListItem {
  id: string;
  full_name: string;
  title: string;
  role_category: string | null;
  photo_url: string | null;
  reports_to_id: string | null;
  has_bio: boolean;
  has_contacts: boolean;
  data_completeness: number;
}

export interface OrgChartResponse {
  company_id: string;
  company_name: string;
  executives: ExecutiveListItem[];
  subsidiaries: unknown[];
  total_executives: number;
}

export interface ExecutiveProfileResponse {
  executive: ExecutiveListItem & {
    first_name?: string;
    last_name?: string;
    contacts?: Array<{
      contact_type: string;
      value: string;
      is_verified: boolean;
    }>;
    bio?: {
      summary: string;
      fragments: Array<{
        content: string;
        source_type: string;
      }>;
    };
    career_history?: Array<{
      company: string;
      title: string;
      start_date?: string;
      end_date?: string;
    }>;
  };
  linkedin_enriched: boolean;
  linkedin_queued: boolean;
}

export async function getOrgChart(companyId: string): Promise<OrgChartResponse> {
  return fetchAPI(`/api/executives/company/${companyId}/org-chart`);
}

export async function getExecutives(companyId: string): Promise<{
  executives: ExecutiveListItem[];
  total_count: number;
  company_id: string;
  has_linkedin_data: boolean;
}> {
  return fetchAPI(`/api/executives/company/${companyId}`);
}

export async function getExecutiveProfile(executiveId: string): Promise<ExecutiveProfileResponse> {
  return fetchAPI(`/api/executives/${executiveId}`);
}

export async function searchExecutives(
  query: string,
  options?: { companyId?: string; roleCategory?: string[]; limit?: number }
): Promise<{
  query: string;
  results: ExecutiveListItem[];
  total_count: number;
  search_type: string;
}> {
  const params = new URLSearchParams({ q: query });
  if (options?.companyId) params.set('company_id', options.companyId);
  if (options?.roleCategory) {
    options.roleCategory.forEach((r) => params.append('role_category', r));
  }
  if (options?.limit) params.set('limit', options.limit.toString());
  return fetchAPI(`/api/executives/search?${params}`);
}

// LinkedIn Authentication
export interface LinkedInLoginRequest {
  email: string;
  password: string;
}

export interface LinkedInLoginResponse {
  success: boolean;
  requires_2fa: boolean;
  session_id?: string;
  error?: string;
}

export interface LinkedIn2FARequest {
  code: string;
  session_id: string;
}

export interface LinkedInAuthStatus {
  authenticated: boolean;
  expires_at: string | null;
  last_used: string | null;
  user_email: string | null;
  needs_validation?: boolean;
  validation_error?: string | null;
}

export interface LinkedInValidationResponse {
  valid: boolean;
  error: string | null;
  profile_name: string | null;
}

export async function linkedInLogin(request: LinkedInLoginRequest): Promise<LinkedInLoginResponse> {
  return fetchAPI('/api/linkedin/auth/login', {
    method: 'POST',
    body: JSON.stringify(request),
  });
}

export async function linkedInSubmit2FA(
  request: LinkedIn2FARequest
): Promise<LinkedInLoginResponse> {
  return fetchAPI('/api/linkedin/auth/2fa', {
    method: 'POST',
    body: JSON.stringify(request),
  });
}

export interface LinkedInAppApprovalRequest {
  session_id: string;
  timeout_seconds?: number;
}

export async function linkedInWaitForAppApproval(
  request: LinkedInAppApprovalRequest
): Promise<LinkedInLoginResponse> {
  return fetchAPI('/api/linkedin/auth/app-approval', {
    method: 'POST',
    body: JSON.stringify(request),
  });
}

export async function getLinkedInStatus(): Promise<LinkedInAuthStatus> {
  return fetchAPI('/api/linkedin/auth/status');
}

export async function validateLinkedInConnection(): Promise<LinkedInValidationResponse> {
  return fetchAPI('/api/linkedin/auth/validate', { method: 'POST' });
}

export async function linkedInLogout(): Promise<{ success: boolean; message: string }> {
  return fetchAPI('/api/linkedin/auth/logout', { method: 'DELETE' });
}

export interface LinkedInManualCookieRequest {
  cookie: string;
  linkedin_email?: string;
}

export async function linkedInSubmitManualCookie(
  request: LinkedInManualCookieRequest
): Promise<LinkedInLoginResponse> {
  return fetchAPI('/api/linkedin/auth/manual-cookie', {
    method: 'POST',
    body: JSON.stringify(request),
  });
}

// LinkedIn Browser-Based Authentication
export interface LinkedInBrowserSessionResponse {
  success: boolean;
  session_id?: string;
  message?: string;
  error?: string;
}

export interface LinkedInBrowserSessionRequest {
  session_id: string;
}

export async function linkedInStartBrowser(): Promise<LinkedInBrowserSessionResponse> {
  return fetchAPI('/api/linkedin/auth/start-browser', {
    method: 'POST',
  });
}

export async function linkedInExtractCookie(
  request: LinkedInBrowserSessionRequest
): Promise<LinkedInLoginResponse> {
  return fetchAPI('/api/linkedin/auth/extract-cookie', {
    method: 'POST',
    body: JSON.stringify(request),
  });
}

export async function linkedInCancelBrowser(
  request: LinkedInBrowserSessionRequest
): Promise<LinkedInBrowserSessionResponse> {
  return fetchAPI('/api/linkedin/auth/cancel-browser', {
    method: 'DELETE',
    body: JSON.stringify(request),
  });
}

// Company Search and Management
export interface AddCompanyRequest {
  name: string;
  domain: string;
  ticker?: string;
  company_type: 'public' | 'pe_backed' | 'private' | 'subsidiary';
}

export interface AddCompanyResponse {
  id: string;
  name: string;
  kb_is_building: boolean;
  message: string;
}

export async function searchCompanies(query: string): Promise<CompanySearchResult> {
  const params = new URLSearchParams({ q: query });
  return fetchAPI(`/api/companies/search?${params}`);
}

export async function addCompany(request: AddCompanyRequest): Promise<AddCompanyResponse> {
  return fetchAPI('/api/companies', {
    method: 'POST',
    body: JSON.stringify(request),
  });
}

export interface DeleteCompanyResponse {
  id: string;
  name: string;
  deleted: boolean;
  message: string;
}

/**
 * Permanently delete a company from the system (SYSTEM ADMIN ONLY).
 *
 * This removes the company AND all KB data. Cannot be undone.
 * Requires system_admin role.
 */
export async function deleteCompanyFromSystem(companyId: string): Promise<DeleteCompanyResponse> {
  return fetchAPI(`/api/companies/${companyId}?confirm=true`, {
    method: 'DELETE',
  });
}

// Watchlist Management

export interface WatchlistRemovalInit {
  removal_pending: boolean;
  company_id: string;
  company_name: string;
  entry_type: string;
  message: string;
  confirm_url: string;
}

export interface WatchlistRemovalResult {
  success: boolean;
  company_id: string;
  message: string;
}

/**
 * Initiate removal of a company from the user's watchlist.
 * This is step 1 of a 2-step removal process.
 */
export async function initiateWatchlistRemoval(companyId: string): Promise<WatchlistRemovalInit> {
  return fetchAPI(`/api/watchlist/me/${companyId}`, {
    method: 'DELETE',
  });
}

/**
 * Confirm removal of a company from the user's watchlist.
 * This is step 2 of a 2-step removal process.
 */
export async function confirmWatchlistRemoval(companyId: string): Promise<WatchlistRemovalResult> {
  return fetchAPI(`/api/watchlist/me/${companyId}/confirm`, {
    method: 'POST',
    body: JSON.stringify({ confirmed: true }),
  });
}

export interface CompanyOrderItem {
  company_id: string;
  display_order: number;
}

/**
 * Reorder companies in the user's watchlist.
 * Used for drag-and-drop reordering.
 */
export async function reorderWatchlist(orders: CompanyOrderItem[]): Promise<void> {
  return fetchAPI(`/api/watchlist/me/reorder`, {
    method: 'PUT',
    body: JSON.stringify({ orders }),
  });
}

// Update Tracking
export interface UpdateItem {
  id: string;
  section: string;
  update_type: string;
  title: string;
  materiality: string;
  timestamp: string;
  details: Record<string, unknown>;
}

export interface CompanyUpdateBadge {
  id: string;
  unread_count: number;
  highest_materiality: string | null;
  latest_update: {
    title: string;
    section: string;
    update_type: string;
    timestamp: string;
  } | null;
  has_summary: boolean;
}

export interface AllCompanyUpdatesResponse {
  companies: CompanyUpdateBadge[];
  total_unread: number;
  since: string;
}

export interface UpdateSummaryResponse {
  company_id: string;
  company_name: string;
  total_unread: number;
  by_section: Record<string, number>;
  highest_materiality: string | null;
  summary_text: string;
  items: UpdateItem[];
  generated_at: string;
}

export async function getAllUpdates(sinceHours = 168): Promise<AllCompanyUpdatesResponse> {
  const params = new URLSearchParams({ since_hours: sinceHours.toString() });
  return fetchAPI(`/api/updates/companies?${params}`);
}

export async function getCompanyUpdateSummary(companyId: string): Promise<UpdateSummaryResponse> {
  return fetchAPI(`/api/updates/companies/${companyId}`);
}

export async function markUpdatesViewed(
  companyId: string,
  section?: string
): Promise<{ success: boolean }> {
  const params = section ? new URLSearchParams({ section }) : '';
  return fetchAPI(`/api/updates/companies/${companyId}/mark-viewed${params ? `?${params}` : ''}`, {
    method: 'POST',
  });
}

export async function markSummaryViewed(companyId: string): Promise<{ success: boolean }> {
  return fetchAPI(`/api/updates/companies/${companyId}/mark-summary-viewed`, {
    method: 'POST',
  });
}

// Customers
export async function getCustomers(companyId: string): Promise<CustomerListResponse> {
  return fetchAPI(`/api/companies/${companyId}/customers`);
}

export async function getCustomerWins(companyId: string): Promise<CustomerWinsResponse> {
  return fetchAPI(`/api/companies/${companyId}/customers/wins`);
}

export async function getCustomerLosses(companyId: string): Promise<CustomerLossesResponse> {
  return fetchAPI(`/api/companies/${companyId}/customers/losses`);
}

export async function getTopCustomers(companyId: string): Promise<TopCustomersResponse> {
  return fetchAPI(`/api/companies/${companyId}/customers/top`);
}

export async function getCustomerTabData(companyId: string): Promise<CustomerTabResponse> {
  return fetchAPI(`/api/companies/${companyId}/customers/tab`);
}

export async function exportCustomersCSV(companyId: string): Promise<Blob> {
  const url = `${API_BASE_URL}/api/companies/${companyId}/customers/export`;
  const headers: HeadersInit = {};
  if (_accessToken) {
    headers['Authorization'] = `Bearer ${_accessToken}`;
  }
  const response = await fetch(url, { headers });
  if (!response.ok) {
    throw new APIError(response.status, 'Failed to export customers');
  }
  return response.blob();
}

// Spike 13: Jobs Intelligence
export interface JobsQueryOptions {
  status?: 'active' | 'removed' | 'all';
  department?: Department;
  verification_status?: 'verified' | 'corroborated' | 'unverified';
  page?: number;
  page_size?: number;
}

export async function getCompanyJobs(
  companyId: string,
  options?: JobsQueryOptions
): Promise<JobListResponse> {
  const params = new URLSearchParams();
  if (options?.status) params.append('status', options.status);
  if (options?.department) params.append('department', options.department);
  if (options?.verification_status)
    params.append('verification_status', options.verification_status);
  if (options?.page) params.append('page', options.page.toString());
  if (options?.page_size) params.append('page_size', options.page_size.toString());

  const queryString = params.toString();
  return fetchAPI(`/api/companies/${companyId}/jobs${queryString ? `?${queryString}` : ''}`);
}

export async function getHiringMetrics(companyId: string): Promise<HiringMetricsResponse> {
  return fetchAPI(`/api/companies/${companyId}/hiring-metrics`);
}

export async function getHiringSignals(companyId: string): Promise<HiringSignalsResponse> {
  return fetchAPI(`/api/companies/${companyId}/hiring-signals`);
}

// =============================================================================
// Admin Cost Tracking
// =============================================================================

import type {
  CostSummary,
  CompanyCostItem,
  ProviderCostItem,
  BuildHistoryItem,
  CostProjection,
} from '../types';

export async function getAdminCostSummary(days: number = 30): Promise<CostSummary> {
  return fetchAPI(`/api/admin/costs/summary?days=${days}`);
}

export async function getAdminCostsByCompany(
  days: number = 30,
  limit: number = 20
): Promise<CompanyCostItem[]> {
  return fetchAPI(`/api/admin/costs/by-company?days=${days}&limit=${limit}`);
}

export async function getAdminCostsByProvider(days: number = 30): Promise<ProviderCostItem[]> {
  return fetchAPI(`/api/admin/costs/by-provider?days=${days}`);
}

export async function getAdminBuildHistory(
  companyId?: string,
  buildType?: string,
  limit: number = 50
): Promise<BuildHistoryItem[]> {
  const params = new URLSearchParams();
  if (companyId) params.append('company_id', companyId);
  if (buildType) params.append('build_type', buildType);
  params.append('limit', limit.toString());
  return fetchAPI(`/api/admin/builds/history?${params.toString()}`);
}

export async function getAdminCostProjection(months: number = 3): Promise<CostProjection> {
  return fetchAPI(`/api/admin/costs/projection?months=${months}`);
}

// =============================================================================
// Admin Jobs
// =============================================================================

export async function getAdminActiveJobs(limit: number = 50): Promise<AdminJobItem[]> {
  return fetchAPI(`/api/admin/jobs/active?limit=${limit}`);
}

export async function getAdminRecentJobs(
  limit: number = 50,
  status?: string
): Promise<AdminJobItem[]> {
  const params = new URLSearchParams();
  params.append('limit', limit.toString());
  if (status) params.append('status', status);
  return fetchAPI(`/api/admin/jobs/recent?${params.toString()}`);
}

export async function getAdminScheduledJobs(): Promise<ScheduledJobItem[]> {
  return fetchAPI('/api/admin/jobs/scheduled');
}

export async function getAdminJobStats(): Promise<JobQueueStats> {
  return fetchAPI('/api/admin/jobs/stats');
}

export async function cancelAdminJob(jobId: string): Promise<{ message: string }> {
  return fetchAPI(`/api/admin/jobs/${jobId}/cancel`, {
    method: 'POST',
  });
}

// =============================================================================
// Admin Exception Logs
// =============================================================================

export async function getAdminExceptionLogs(
  limit: number = 100,
  errorCode?: string
): Promise<ExceptionLogItem[]> {
  const params = new URLSearchParams();
  params.append('limit', limit.toString());
  if (errorCode) params.append('error_code', errorCode);
  return fetchAPI(`/api/admin/logs/exceptions?${params.toString()}`);
}

export async function getAdminLogStats(): Promise<LogStats> {
  return fetchAPI('/api/admin/logs/stats');
}

export async function getAdminErrorCodes(): Promise<string[]> {
  return fetchAPI('/api/admin/logs/error-codes');
}

// --- Build Progress ---

export interface BuildProgressItem {
  company_id: string;
  job_id: string | null;
  current_phase: string;
  current_phase_label: string;
  phase_index: number;
  total_phases: number;
  progress_pct: number;
  status: string;
  message: string | null;
  updated_at: string;
}

export interface AllBuildProgressResponse {
  active_builds: BuildProgressItem[];
  total_active: number;
}

export interface PhaseHistoryItem {
  phase: string;
  phase_label: string;
  duration_seconds: number;
  status: string;
  message: string | null;
  completed_at: string;
}

export async function getAdminBuildProgress(): Promise<AllBuildProgressResponse> {
  return fetchAPI('/api/admin/builds/progress');
}

export async function getAdminCompanyBuildProgress(
  companyId: string
): Promise<BuildProgressItem | null> {
  return fetchAPI(`/api/admin/builds/progress/${companyId}`);
}

export async function getAdminPhaseHistory(
  companyId: string,
  limit: number = 50
): Promise<PhaseHistoryItem[]> {
  const params = new URLSearchParams({ limit: String(limit) });
  return fetchAPI(`/api/admin/builds/history/${companyId}?${params}`);
}

// =============================================================================
// Admin User Statistics
// =============================================================================

export async function getAdminUserStatsSummary(days: number = 30): Promise<UserStatsSummary> {
  return fetchAPI(`/api/admin/stats/users/summary?days=${days}`);
}

export async function getAdminGroupStats(): Promise<GroupStats[]> {
  return fetchAPI('/api/admin/stats/groups');
}

export async function getAdminActiveUsers(
  limit: number = 10,
  days: number = 30
): Promise<ActiveUserStats[]> {
  const params = new URLSearchParams({
    limit: limit.toString(),
    days: days.toString(),
  });
  return fetchAPI(`/api/admin/stats/users/active?${params.toString()}`);
}

export async function getAdminUserTrends(days: number = 30): Promise<DailyActiveUsers[]> {
  return fetchAPI(`/api/admin/stats/users/trends?days=${days}`);
}

export async function getAdminTopCompanies(
  limit: number = 10,
  days: number = 30
): Promise<TopCompanyStats[]> {
  const params = new URLSearchParams({
    limit: limit.toString(),
    days: days.toString(),
  });
  return fetchAPI(`/api/admin/stats/companies/top?${params.toString()}`);
}

export async function getAdminTopQuestions(
  limit: number = 10,
  days: number = 30
): Promise<TopQuestion[]> {
  const params = new URLSearchParams({
    limit: limit.toString(),
    days: days.toString(),
  });
  return fetchAPI(`/api/admin/stats/chat/top-questions?${params.toString()}`);
}

// =============================================================================
// Activity Tracking
// =============================================================================

export async function logActivityEvent(
  eventType: ActivityEventType,
  metadata?: Record<string, unknown>,
  sessionId?: string
): Promise<LogEventResponse> {
  return fetchAPI('/api/activity/event', {
    method: 'POST',
    body: JSON.stringify({
      event_type: eventType,
      metadata: metadata || {},
      session_id: sessionId,
    }),
  });
}

export async function startSession(): Promise<StartSessionResponse> {
  return fetchAPI('/api/activity/session/start', {
    method: 'POST',
  });
}

export async function endSession(sessionId: string): Promise<EndSessionResponse> {
  return fetchAPI(`/api/activity/session/end/${sessionId}`, {
    method: 'POST',
  });
}

// =============================================================================
// Company Comparison
// =============================================================================

import type { CompanyComparisonResponse } from '../types';

export async function compareCompanies(companyIds: string[]): Promise<CompanyComparisonResponse> {
  return fetchAPI('/api/companies/compare', {
    method: 'POST',
    body: JSON.stringify({ company_ids: companyIds }),
  });
}

export async function exportComparison(companyIds: string[]): Promise<Blob> {
  const ids = companyIds.join(',');
  const url = `${API_BASE_URL}/api/companies/compare/export?ids=${ids}`;
  const headers: HeadersInit = {};
  if (_accessToken) {
    headers['Authorization'] = `Bearer ${_accessToken}`;
  }
  const response = await fetch(url, { headers });
  if (!response.ok) {
    throw new APIError(response.status, 'Failed to export comparison');
  }
  return response.blob();
}

// Export the API client
export const api = {
  checkHealth,
  listCompanies,
  getCompany,
  getCompanyOverview,
  getCompanyNews,
  getNewsDetail,
  getCompanyProducts,
  getKBStatus,
  sendChatMessage,
  listConversations,
  getConversation,
  deleteConversation,
  getStockData,
  getQuarterlyRevenue,
  getReportTemplates,
  getReportTemplate,
  generateReport,
  listReports,
  getReport,
  deleteReport,
  // Executives
  getOrgChart,
  getExecutives,
  getExecutiveProfile,
  searchExecutives,
  // LinkedIn
  linkedInLogin,
  linkedInSubmit2FA,
  getLinkedInStatus,
  linkedInLogout,
  // Company management
  searchCompanies,
  addCompany,
  deleteCompanyFromSystem,
  // Updates
  getAllUpdates,
  getCompanyUpdateSummary,
  markUpdatesViewed,
  markSummaryViewed,
  // Customers
  getCustomers,
  getCustomerWins,
  getCustomerLosses,
  getTopCustomers,
  getCustomerTabData,
  exportCustomersCSV,
  // Spike 13: Portfolio & Competitive
  getCompanyPortfolio,
  getProductProfile,
  getProductCompetitors,
  getCompetitiveMatrix,
  // Spike 13: Jobs Intelligence
  getCompanyJobs,
  getHiringMetrics,
  getHiringSignals,
  // Admin Cost Tracking
  getAdminCostSummary,
  getAdminCostsByCompany,
  getAdminCostsByProvider,
  getAdminBuildHistory,
  getAdminCostProjection,
  // Admin Jobs
  getAdminActiveJobs,
  getAdminRecentJobs,
  getAdminScheduledJobs,
  getAdminJobStats,
  cancelAdminJob,
  // Admin Exception Logs
  getAdminExceptionLogs,
  getAdminLogStats,
  getAdminErrorCodes,
  // Admin Build Progress
  getAdminBuildProgress,
  getAdminCompanyBuildProgress,
  getAdminPhaseHistory,
  // Admin User Statistics
  getAdminUserStatsSummary,
  getAdminGroupStats,
  getAdminActiveUsers,
  getAdminUserTrends,
  getAdminTopCompanies,
  getAdminTopQuestions,
  // Activity Tracking
  logActivityEvent,
  startSession,
  endSession,
  // Company Comparison
  compareCompanies,
  exportComparison,
};

export default api;
